/**
 * Marly Design Studio — Slider utilities
 * Handles: hero fade slideshow + horizontal collection scroll
 */

/* ── Hero Slideshow ───────────────────────────────────────────── */
class MarlyHero {
  constructor(el) {
    this.el      = el;
    this.track   = el.querySelector('.marly-hero__slides');
    this.slides  = [...el.querySelectorAll('.marly-hero__slide')];
    this.dots    = [...el.querySelectorAll('.marly-hero__dot')];
    this.prevBtn = el.querySelector('.marly-hero__arrow--prev');
    this.nextBtn = el.querySelector('.marly-hero__arrow--next');
    this.current  = 0;
    this.autoplay = el.dataset.autoplay === 'true';
    this.speed    = parseInt(el.dataset.speed, 10) || 5000;
    this.timer    = null;

    if (this.slides.length <= 1) return;
    this.bind();
    if (this.autoplay) this.startTimer();
  }

  bind() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());
    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => this.goTo(i))
    );

    /* Pause on hover */
    this.el.addEventListener('mouseenter', () => this.stopTimer());
    this.el.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startTimer();
    });

    /* Touch / swipe */
    let startX = 0;
    this.el.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    this.el.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 48) diff > 0 ? this.next() : this.prev();
    });

    /* Keyboard: only when hero has focus */
    this.el.setAttribute('tabindex', '0');
    this.el.addEventListener('keydown', e => {
      if (e.key === 'ArrowRight') this.next();
      if (e.key === 'ArrowLeft')  this.prev();
    });
  }

  goTo(index) {
    this.dots[this.current]?.classList.remove('is-active');
    this.slides[this.current].classList.remove('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'true');
    this.current = (index + this.slides.length) % this.slides.length;
    this.track.style.transform = `translateX(-${this.current * 100}%)`;
    /* Force reflow so the Ken Burns animation restarts when revisiting a slide */
    void this.slides[this.current].offsetWidth;
    this.dots[this.current]?.classList.add('is-active');
    this.slides[this.current].classList.add('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'false');
  }

  next() { this.goTo(this.current + 1); }
  prev() { this.goTo(this.current - 1); }

  startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stopTimer() {
    clearInterval(this.timer);
  }
}

/* ── Horizontal Collection Slider ────────────────────────────── */
class MarlyHorizontalSlider {
  constructor(section) {
    this.section  = section;
    this.track    = section.querySelector('.marly-slider-track');
    this.prevBtn  = section.querySelector('.marly-slider-arrow--prev');
    this.nextBtn  = section.querySelector('.marly-slider-arrow--next');

    if (!this.track) return;
    this.bind();
    this.updateArrows();
  }

  bind() {
    this.prevBtn?.addEventListener('click', () => this.scroll(-1));
    this.nextBtn?.addEventListener('click', () => this.scroll(1));
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
  }

  scroll(dir) {
    const item  = this.track.querySelector('.marly-slider-item');
    const width = item ? item.offsetWidth + 24 : 320;
    this.track.scrollBy({ left: dir * width, behavior: 'smooth' });
  }

  updateArrows() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (this.prevBtn) this.prevBtn.style.opacity = scrollLeft > 4 ? '1' : '0.3';
    if (this.nextBtn)
      this.nextBtn.style.opacity =
        scrollLeft < scrollWidth - clientWidth - 4 ? '1' : '0.3';
  }
}

/* ── Init on DOM ready ───────────────────────────────────────── */
function marlyInit() {
  document.querySelectorAll('.marly-hero').forEach(el => new MarlyHero(el));
  /* Attach horizontal slider to any wrapper that contains a track —
     covers collection slider, portfolio slider, and any future slider. */
  document
    .querySelectorAll('.marly-slider-wrapper')
    .forEach(el => new MarlyHorizontalSlider(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', marlyInit);
} else {
  marlyInit();
}
